import { useEffect, useMemo, useState } from 'react'
import {
  DashboardCategoryTabs,
  type DashboardCategory,
} from '../components/DashboardCategoryTabs'
import { GeneralOverviewPanel } from '../components/GeneralOverviewPanel'
import { TileGroup } from '../components/TileGroup'
import { TILE_GROUPS } from '../dashboard/catalog'
import { hasCapability, useAuth } from '../context/AuthContext'
import {
  DASHBOARD_CATEGORY_PATHS,
  dashboardCategoryForPath,
  type DashboardCategoryKey,
} from '../lib/dashboardRoutes'
import { navigate, useRoute } from '../lib/router'
import type { DashboardView } from '../types/api'

export type DashboardTabKey = DashboardCategoryKey



/**
 * The dashboard, built from the dictated map in §9.6 (`dashboard/catalog.ts`).
 *
 * General Overview is live (D-60); Insights, Suppliers, Modules and Tools render their
 * own TileGroup. Switching category slides the panel in from the direction of travel
 * and pops the active tab.
 */
export function DashboardPage({
  dashboard,
  initialTab,
}: {
  dashboard: DashboardView
  /** Smoke/test hook: render a specific category without simulating a click. */
  initialTab?: DashboardTabKey
}) {
  const { pendingProcurementOrders } = dashboard
  const { user } = useAuth()

  const visibleGroups = useMemo(
    () =>
      TILE_GROUPS.filter(
        (group) => !user || !group.permission || hasCapability(user, group.permission),
      ),
    [user],
  )
  const categories = useMemo<DashboardCategory[]>(
    () => [
      { key: 'overview', label: 'General Overview' },
      ...visibleGroups.map((group) => ({ key: group.key, label: group.title })),
    ],
    [visibleGroups],
  )

  const route = useRoute()
  const routeTab = dashboardCategoryForPath(route.path)
  const [activeTab, setActiveTab] = useState<DashboardTabKey>(() => {
    const requested = initialTab ?? routeTab ?? route.query.get('category')
    return categories.some((category) => category.key === requested)
      ? (requested as DashboardTabKey)
      : 'overview'
  })
  const [direction, setDirection] = useState<'next' | 'prev'>('next')

  // Browser Back/Forward and direct category URLs drive the visible category.
  useEffect(() => {
    if (routeTab && categories.some((category) => category.key === routeTab)) {
      if (routeTab !== activeTab) setActiveTab(routeTab)
    }
  }, [routeTab, activeTab, categories])

  // Numbers the API already returns, shown inside the tile they belong to instead of a
  // second KPI row — the landing category stays the six dictated numbers (S-01).
  const liveNotes: Record<string, string> = {
    van_gelder: `${pendingProcurementOrders} orders pending`,
  }

  const activeGroup = visibleGroups.find((group) => group.key === activeTab)
  const visibleTiles =
    activeGroup?.tiles.filter(
      (tile) => !user || !tile.permission || hasCapability(user, tile.permission),
    ) ?? []

  const selectTab = (key: string) => {
    const currentIndex = categories.findIndex((category) => category.key === activeTab)
    const nextIndex = categories.findIndex((category) => category.key === key)
    if (nextIndex < 0) return
    setDirection(nextIndex >= currentIndex ? 'next' : 'prev')
    const nextTab = key as DashboardTabKey
    setActiveTab(nextTab)
    navigate(DASHBOARD_CATEGORY_PATHS[nextTab])
  }

  const openTile = (target: string) => {
    navigate(target)
  }

  return (
    <div className="space-y-4">
      {/* On a phone the fixed bottom bar is the category switcher (D-106); showing
          this five-tab bar as well would spend a whole row of a small screen on the
          same five destinations. It stays exactly as it was from `sm` up. */}
      <div className="hidden sm:block">
        <DashboardCategoryTabs
          categories={categories}
          activeKey={activeTab}
          onSelect={selectTab}
          panelId={`dashboard-panel-${activeTab}`}
        />
      </div>

      <div
        key={activeTab}
        role="tabpanel"
        id={`dashboard-panel-${activeTab}`}
        aria-labelledby={`dashboard-tab-${activeTab}`}
        className={[
          'space-y-6 will-change-transform',
          direction === 'next' ? 'animate-category-next' : 'animate-category-prev',
          'motion-reduce:animate-none',
        ].join(' ')}
      >
        {activeTab === 'overview' ? (
          <GeneralOverviewPanel />
        ) : activeGroup && visibleTiles.length > 0 ? (
          <TileGroup
            group={{ ...activeGroup, tiles: visibleTiles }}
            liveNotes={liveNotes}
            onOpen={openTile}
          />
        ) : activeGroup ? (
          <p className="card px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
            No tabs in {activeGroup.title} are available for your role.
          </p>
        ) : null}
      </div>
    </div>
  )
}

